import { OptionType } from '@evinced-private/ui-common';

import {
	ENVIRONMENT_LABEL_KEY,
	LABEL_DELIMITER,
	UNIT_ID_LABEL_KEY,
	WARNING_EMOJI
} from 'src/common/consts/platformConfigConsts';
import { ConfigurationLabel, ConfigurationLabelValue } from 'src/common/types/ConfigurationTypes';

const NO_OPTION_VALUE_SUFFIX = '__ev_no_option__';

const normalize = (value: string): string => value.trim().toLowerCase();

const isNoOptionSelection = (optionValue: OptionType | string | number): boolean => {
	if (typeof optionValue !== 'string') {
		return false;
	}

	const colonIndex = optionValue.indexOf(LABEL_DELIMITER);
	if (colonIndex <= 0) {
		return false;
	}

	const valuePart = optionValue.substring(colonIndex + 1).trim();
	return normalize(valuePart) === normalize(NO_OPTION_VALUE_SUFFIX);
};

export const getConfiguredValuesFromCoreLabel = (
	label: ConfigurationLabel | undefined
): Set<string> => {
	if (!label?.values) {
		return new Set<string>();
	}

	return new Set(
		label.values.map((v) => (typeof v === 'string' ? v : (v as ConfigurationLabelValue).value))
	);
};

export const markOptionsWithWarnings = (
	options: OptionType[],
	configuredValuesSet: Set<string>,
	displayName: string,
	extractValueFromLabel?: (label: string) => string
): OptionType[] => {
	return options.map((option) => {
		if (isNoOptionSelection(option.value)) {
			return option;
		}

		// Use the label as it contains the value that should match the configured values
		const optionLabel = String(option.label);

		// Extract the value part if a custom extractor is provided (for labels like "Environment: development")
		// Otherwise, use the label as-is
		const valueToCheck = extractValueFromLabel ? extractValueFromLabel(optionLabel) : optionLabel;

		// Check if the value matches any configured value (case-insensitive)
		const matchesConfiguration = Array.from(configuredValuesSet).some(
			(configuredValue) => configuredValue.toLowerCase() === valueToCheck.toLowerCase()
		);

		// Only add warning if:
		// 1. The option doesn't match any configured value
		// 2. There are configured values to compare against (to avoid false positives when config is empty)
		if (!matchesConfiguration && configuredValuesSet.size > 0) {
			// Generate tooltip content using the display name from configuration
			const tooltipContent = `${displayName}${LABEL_DELIMITER} ${valueToCheck}\n${WARNING_EMOJI} Unknown value`;

			return {
				...option,
				label: `${WARNING_EMOJI} ${option.label}`,
				customTooltip: tooltipContent
			};
		}

		return option;
	});
};

export interface ConfiguredValues {
	environment: Set<string>;
	unitId: Set<string>;
	environmentDisplayName: string;
	unitIdDisplayName: string;
}

export const markOptionsWithWarningsFromKeyValue = (
	options: OptionType[],
	configuredValues: ConfiguredValues,
	extractLabelKey: (value: string) => string,
	parseLabel: (label: string) => { key: string; value: string } | null
): OptionType[] => {
	return options.map((option) => {
		// Never warn on the special "No option" selection (e.g. "Environment: __ev_no_option__")
		if (isNoOptionSelection(option.value)) {
			return option;
		}

		const labelString = option.value as string;
		const labelKey = extractLabelKey(labelString);
		const parsed = parseLabel(labelString);
		const labelValue = parsed?.value ?? '';

		const configMap: Record<string, { set: Set<string>; name: string }> = {
			[ENVIRONMENT_LABEL_KEY.toLowerCase()]: {
				set: configuredValues.environment,
				name: configuredValues.environmentDisplayName
			},
			[UNIT_ID_LABEL_KEY.toLowerCase()]: {
				set: configuredValues.unitId,
				name: configuredValues.unitIdDisplayName
			}
		};

		const config = configMap[labelKey];
		const shouldWarn =
			config?.set.size > 0 &&
			!Array.from(config.set).some(
				(v) => v.localeCompare(labelValue, undefined, { sensitivity: 'base' }) === 0
			);

		if (shouldWarn && config) {
			const tooltipContent = `${config.name}${LABEL_DELIMITER} ${labelValue}\n${WARNING_EMOJI} Unknown value`;
			const currentLabel = String(option.label);

			const newLabel = currentLabel.includes(LABEL_DELIMITER)
				? currentLabel.replace(LABEL_DELIMITER, `${LABEL_DELIMITER} ${WARNING_EMOJI}`)
				: `${WARNING_EMOJI} ${currentLabel}`;

			return {
				...option,
				label: newLabel,
				customTooltip: tooltipContent
			};
		}

		return option;
	});
};
